import { Injectable } from '@angular/core';
import {
    AccountMappingTemplateGroupItem,
    AccountType,
    IAccount,
    IAccountMappingSubFLI,
    IAccountMappingTemplate,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    IReclassesAccounts,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { flatten } from 'lodash-es';
import { AccountsHelper } from './accounts.helper';

@Injectable({ providedIn: 'root' })
export class FlisAccountsHelper {
    getFliIdToAccountsForTemplates(
        balanceTemplate: IAccountMappingTemplate,
        incomeTemplate: IAccountMappingTemplate,
    ): Map<string, IAccount[]> {
        const balanceFlisIdToAccounts = this.getAccountsOfTemplate(balanceTemplate);
        const incomeFlisIdToAccounts = this.getAccountsOfTemplate(incomeTemplate);

        return this.mergeArrayOfMaps([balanceFlisIdToAccounts, incomeFlisIdToAccounts]);
    }

    private getAccountsOfTemplate(template: IAccountMappingTemplate): Map<string, IAccount[]> {
        if (template == null) {
            return new Map<string, IAccount[]>();
        }
        const templateGroups = [template.group];

        return this.getAccountsOfGroups(templateGroups);
    }

    private getAccountsOfGroups(templateGroups: IAccountMappingTemplateGroup[]): Map<string, IAccount[]> {
        const fliIdsToAccounts: Map<string, IAccount[]>[] = [];
        for (const group of templateGroups) {
            const fliIdtoAccountsOfGroup = this.extractAccountsOfGroup(group.items);
            fliIdsToAccounts.push(fliIdtoAccountsOfGroup);

            if (group.groups != null && group.groups.length > 0) {
                const fliIdToAccountsOfInnerGroups = this.getAccountsOfGroups(group.groups);
                fliIdsToAccounts.push(fliIdToAccountsOfInnerGroups);
            }
        }

        return this.mergeArrayOfMaps(fliIdsToAccounts);
    }

    private extractAccountsOfGroup(itemsOfGroup: AccountMappingTemplateGroupItem[]): Map<string, IAccount[]> {
        const fliIdsToAccounts: Map<string, IAccount[]>[] = [];
        for (const item of itemsOfGroup) {
            if (item.type !== ItemType.SUB_TOTAL && item.type !== ItemType.HEADER && item.type !== ItemType.EMPTY) {
                const currentFliIdToAccounts = this.getAccountsOfFli(item as IAccountMappingTemplateGroupItemFLI);
                fliIdsToAccounts.push(currentFliIdToAccounts);
            }
        }

        return this.mergeArrayOfMaps(fliIdsToAccounts);
    }

    private getAccountsOfFli(fli: IAccountMappingTemplateGroupItemFLI): Map<string, IAccount[]> {
        const fliIdToAccounts = new Map<string, IAccount[]>();
        // first get normal accounts
        const accountsMappedDirectlyToFli: IAccount[] = [];
        const reclassesAccountAsNormalAccountsOfFli: IAccount[] = [];
        const accountsMappedToSubFlis: IAccount[] = [];
        const reclassesAccountAsNormalAccountsOfSubFlis: IAccount[] = [];
        for (const account of fli.accounts) {
            if (
                account.type !== ItemType.SUB_FLI &&
                account.type !== ItemType.EMPTY &&
                account.type !== ItemType.HEADER &&
                account.type !== ItemType.OF_THAT_ITEM
            ) {
                accountsMappedDirectlyToFli.push(account as IAccount);
            } else if (account.type === ItemType.SUB_FLI || account.type === ItemType.OF_THAT_ITEM) {
                // get account of sub-flis
                const subfliNormalAccounts = account.accounts;
                accountsMappedToSubFlis.push(...subfliNormalAccounts);
                if (account.reclassesAccounts) {
                    const subfliReclassesAccounts = this.getReclassesAccountsAsNormalAccounts(
                        account.reclassesAccounts,
                    );

                    reclassesAccountAsNormalAccountsOfSubFlis.push(...subfliReclassesAccounts);
                    // to add subfli as fli in the map with its own accounts
                }

                const subfliAllAccounts = this.getAccountsOfSubFli(account);
                fliIdToAccounts.set(account.name, subfliAllAccounts);
            }
        }
        const fliReclassesAccounts = this.getReclassesAccountsAsNormalAccounts(fli.reclassesAccounts);
        reclassesAccountAsNormalAccountsOfFli.push(...fliReclassesAccounts);
        // the following order should be: accounts of fli, account of sub flis of the fli, reclasses accounts of fli and reclasses accounts of sub flis of the fli
        const accounts = [
            ...accountsMappedDirectlyToFli,
            ...accountsMappedToSubFlis,
            ...reclassesAccountAsNormalAccountsOfFli,
            ...reclassesAccountAsNormalAccountsOfSubFlis,
        ];
        if (accounts.length == 0) return fliIdToAccounts.set(fli.name, [] as IAccount[]);

        const roundedAccounts = AccountsHelper.roundAccountsValues(accounts);
        return fliIdToAccounts.set(fli.name, roundedAccounts);
    }

    private getReclassesAccountsAsNormalAccounts(reclassesAccounts: IReclassesAccounts[]): IAccount[] {
        const adjustmentsAccountsAsNormalAccounts: IAccount[] = [];
        for (const reclassesAccount of reclassesAccounts) {
            // REV-20899: phantom reclass accounts created in the
            // grouping/masking step are stored on `fli.reclassesAccounts` with
            // their host statement's type (`AccountType.BALANCE` on the BS,
            // `AccountType.INCOME` on the income statement) so they appear in
            // downstream lists alongside regular imported accounts. The
            // `IReclassesAccounts` interface still declares
            // `type: AccountType.RECLASSES`, so the field is read here as the
            // raw enum value and preserved if it is BALANCE / INCOME — only
            // legacy reclasses-only entries (no override) fall back to
            // `AccountType.RECLASSES`.
            const sourceType = (reclassesAccount as unknown as { type?: AccountType }).type;
            const preservedType: AccountType =
                sourceType === AccountType.BALANCE || sourceType === AccountType.INCOME
                    ? sourceType
                    : AccountType.RECLASSES;
            const normalAccount: IAccount = {
                accountNumber: reclassesAccount.accountNumber,
                name: reclassesAccount.name,
                closingBalance: 0, // reclasses account has no closing value
                openingBalance: 0, // reclasses account has no open value
                type: preservedType,
                calculatedClosingBalance: reclassesAccount.calculatedClosingBalance,
                calculatedOpeningBalance: reclassesAccount.calculatedOpeningBalance,
                closingAdjustment: reclassesAccount.closingAdjustment,
                openingAdjustment: reclassesAccount.openingAdjustment,
                closingReClass: reclassesAccount.closingReClass,
                openingReClass: reclassesAccount.openingReClass,
            };

            adjustmentsAccountsAsNormalAccounts.push(normalAccount);
        }

        return adjustmentsAccountsAsNormalAccounts;
    }

    private getAccountsOfSubFli(subfli: IAccountMappingSubFLI): IAccount[] {
        const subfliReclassesAccounts = this.getReclassesAccountsAsNormalAccounts(subfli.reclassesAccounts);
        return [...subfli.accounts, ...subfliReclassesAccounts];
    }

    private mergeArrayOfMaps(arrayOfMaps: Map<string, IAccount[]>[]): Map<string, IAccount[]> {
        const spreadMaps = arrayOfMaps.map((value) => [...value]);
        const flattenedMaps = flatten<[string, IAccount[]]>(spreadMaps);
        return new Map<string, IAccount[]>(flattenedMaps);
    }
}
